import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { RT } from '../../retention/theme/retentionTheme';
import { SoftChip } from '../../retention/components/ui';
import {
  AttendanceRing,
  BANDS,
  DataCheckNote,
  DISPLAY_FONT,
  EmptyBlock,
  GRADIENTS,
  GlassBar,
  GradientStatCard,
  HERO,
  LoadingBlock,
  MiniStat,
  Panel,
  PercentBar,
  RecentDots,
  STATUS_META,
  bandColor,
  fmtDate,
  fmtMonth,
  fmtPct,
  fmtShortDate,
  fmtTime,
  numberSx,
} from './components/ui';
import { TrendChart, WeekdayChart } from './components/charts';

const ENROLLMENT_STATUS = { 1: ['Active', '#4ADE80'], 0: ['Inactive', '#FDA4AF'], 2: ['Temporary inactive', '#FCD34D'] };

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

// "+4.5 pts vs batch" chip
const DeltaChip = ({ mine, batch }) => {
  if (mine === null || mine === undefined || batch === null || batch === undefined) return null;
  const delta = Math.round((mine - batch) * 10) / 10;
  const color = delta >= 0 ? RT.success : RT.error;
  return (
    <Tooltip title={`Batch average: ${fmtPct(batch)}`} arrow>
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.15, borderRadius: 99, fontSize: 11.5, fontWeight: 800, color, bgcolor: alpha(color, 0.12), ...numberSx }}
      >
        <Icon icon={delta >= 0 ? 'solar:arrow-up-bold' : 'solar:arrow-down-bold'} width={12} />
        {Math.abs(delta)} pts vs batch
      </Box>
    </Tooltip>
  );
};

function SubjectCompareCard({ subject }) {
  const color = bandColor(subject.attendance);
  return (
    <Card sx={{ borderRadius: 4, overflow: 'hidden' }}>
      <Box sx={{ height: 5, background: `linear-gradient(90deg, ${alpha(color, 0.5)}, ${color})` }} />
      <Stack gap={1.5} sx={{ p: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center">
          <AttendanceRing value={subject.attendance} size={66} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 17 }} noWrap>
              {subject.subjectName}
            </Typography>
            <DeltaChip mine={subject.attendance} batch={subject.batchAttendance} />
          </Box>
        </Stack>
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 600 }}>
              Batch average
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, ...numberSx }}>
              {fmtPct(subject.batchAttendance)}
            </Typography>
          </Stack>
          <PercentBar value={subject.batchAttendance} color="#A5B4FC" height={6} />
        </Box>
        <Stack direction="row" justifyContent="space-between">
          <MiniStat label="Attended" value={`${subject.present}/${subject.marked}`} color={RT.success} />
          <MiniStat label="Absent" value={subject.absent} color={RT.error} />
          <MiniStat label="Not marked" value={subject.notMarked} color={RT.warning} align="right" />
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1, borderTop: `1px dashed ${RT.line}` }}>
          <RecentDots recent={subject.recent} />
          <Typography variant="caption" sx={{ color: RT.muted }}>
            {subject.upcoming ? (
              <>
                Next <b style={{ color: RT.ink2 }}>{fmtShortDate(subject.nextSessionDate)}</b> · {subject.upcoming} left
              </>
            ) : (
              'No upcoming sessions'
            )}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

function SessionTimeline({ sessions, subjects }) {
  const [subject, setSubject] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rows = useMemo(
    () =>
      sessions.filter(
        (session) => (subject === 'all' || String(session.subjectEnrollmentId) === String(subject)) && (status === 'all' || session.status === status),
      ),
    [sessions, subject, status],
  );
  const counts = useMemo(
    () => Object.keys(STATUS_META).reduce((acc, key) => ({ ...acc, [key]: sessions.filter((session) => session.status === key).length }), {}),
    [sessions],
  );

  return (
    <Stack gap={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
        <TextField
          select
          size="small"
          label="Subject"
          value={subject}
          onChange={(event) => {
            setSubject(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All subjects</MenuItem>
          {subjects.map((item) => (
            <MenuItem key={item.subjectEnrollmentId} value={item.subjectEnrollmentId}>
              {item.subjectName}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {[['all', { label: 'All', color: RT.primary }], ...Object.entries(STATUS_META)].map(([key, meta]) => (
            <Chip
              key={key}
              size="small"
              label={`${meta.label} · ${key === 'all' ? sessions.length : counts[key] || 0}`}
              onClick={() => {
                setStatus(key);
                setPage(0);
              }}
              sx={{
                fontWeight: 700,
                bgcolor: status === key ? meta.color : alpha(meta.color, 0.1),
                color: status === key ? '#fff' : meta.color,
                '&:hover': { bgcolor: status === key ? meta.color : alpha(meta.color, 0.2) },
              }}
            />
          ))}
        </Stack>
      </Stack>
      {rows.length ? (
        <>
          <TableContainer sx={{ border: `1px solid ${RT.line}`, borderRadius: 3 }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#F8F9FC', fontWeight: 700, color: RT.ink2, fontSize: 12.5 } }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Tutor</TableCell>
                  <TableCell>Lesson</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((session) => {
                  const meta = STATUS_META[session.status] || STATUS_META.na;
                  return (
                    <TableRow key={session.sessionId} sx={{ '& td': { borderColor: RT.line } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack direction="row" gap={1.25} alignItems="center">
                          <Box sx={{ width: 4, height: 30, borderRadius: 4, bgcolor: meta.color }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, ...numberSx }}>
                              {fmtDate(session.sessionDate)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: RT.muted }}>
                              {fmtTime(session.fromTime)} {session.toTime ? `– ${fmtTime(session.toTime)}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{session.subjectName}</TableCell>
                      <TableCell sx={{ color: RT.ink2 }}>{session.tutorName || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="caption" sx={{ color: RT.muted }} noWrap component="div" title={session.lessons || ''}>
                          {session.lessons || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <SoftChip icon={meta.icon} label={session.auto ? 'Absent · did not join on Wise' : meta.label} color={meta.color} sx={{ fontWeight: 800 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      ) : (
        <EmptyBlock title="No sessions match" />
      )}
    </Stack>
  );
}

export default function StudentView({ data, loading }) {
  if (loading && !data) {
    return (
      <Stack gap={2.5}>
        <LoadingBlock rows={3} />
        <LoadingBlock rows={8} />
      </Stack>
    );
  }
  if (!data) return <Panel><EmptyBlock title="Couldn't load this student" /></Panel>;

  const { student, batch, summary, subjects = [], monthly = [], weekday = [], sessions = [] } = data;
  const band = BANDS.find((item) => item.key === summary.band);
  const [enrollmentLabel, enrollmentColor] = ENROLLMENT_STATUS[student.enrollmentStatus] || ['—', '#E2E8F0'];
  const streakCard =
    summary.currentAbsentStreak > 0
      ? { gradient: GRADIENTS.rose, icon: 'solar:fire-bold-duotone', label: 'Absent in a row', value: summary.currentAbsentStreak, sub: 'Counting back from the latest marked session' }
      : { gradient: GRADIENTS.teal, icon: 'solar:fire-bold-duotone', label: 'Present in a row', value: summary.currentPresentStreak, sub: `Longest run: ${summary.longestPresentStreak} sessions` };

  const cards = [
    {
      gradient: GRADIENTS.indigo,
      icon: 'solar:chart-square-bold-duotone',
      label: 'Attendance',
      value: fmtPct(summary.attendance),
      sub: `${summary.present} attended of ${summary.marked} marked sessions`,
      footer: <GlassBar value={summary.attendance} />,
    },
    {
      gradient: GRADIENTS.violet,
      icon: 'solar:ranking-bold-duotone',
      label: 'Rank in batch',
      value: summary.rank ? `#${summary.rank}` : '—',
      sub: summary.classSize ? `of ${summary.classSize} students · batch avg ${fmtPct(summary.batchAttendance)}` : 'No marks yet',
    },
    {
      gradient: GRADIENTS.rose,
      icon: 'solar:close-circle-bold-duotone',
      label: 'Absent',
      value: summary.absent,
      sub: `${summary.notMarked} not marked · ${summary.na} N/A`,
    },
    streakCard,
    {
      gradient: GRADIENTS.emerald,
      icon: 'solar:calendar-mark-bold-duotone',
      label: 'Sessions since joining',
      value: summary.eligible,
      sub: `Last present: ${fmtDate(summary.lastPresentDate)}`,
    },
    {
      gradient: GRADIENTS.amber,
      icon: 'solar:calendar-add-bold-duotone',
      label: 'Upcoming sessions',
      value: summary.upcoming,
      sub: 'Scheduled for this student’s subjects',
    },
  ];

  const trend = monthly.map((month) => ({ label: fmtMonth(month.month), sessions: month.sessions, attendance: month.attendance }));

  return (
    <Stack gap={2.5}>
      <Card sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', background: HERO, color: '#fff', position: 'relative' }}>
        <Box sx={{ position: 'absolute', width: 280, height: 280, borderRadius: '50%', right: -80, top: -120, bgcolor: 'rgba(255,255,255,.08)' }} />
        <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'center' }} sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 4,
              display: 'grid',
              placeItems: 'center',
              fontFamily: DISPLAY_FONT,
              fontSize: 28,
              fontWeight: 700,
              bgcolor: 'rgba(255,255,255,.18)',
              border: '1px solid rgba(255,255,255,.3)',
              flexShrink: 0,
            }}
          >
            {initials(student.studentName)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Chip size="small" label={enrollmentLabel} sx={{ bgcolor: enrollmentColor, color: '#1E1B4B', fontWeight: 800 }} />
              {!student.inBatch && <Chip size="small" label="Left this batch" sx={{ bgcolor: 'rgba(255,255,255,.2)', color: '#fff', fontWeight: 700 }} />}
              {band && <Chip size="small" label={`${band.label} attendance`} sx={{ bgcolor: band.color, color: '#fff', fontWeight: 800 }} />}
            </Stack>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: 24, md: 30 }, fontWeight: 700, lineHeight: 1.15 }}>{student.studentName || '—'}</Typography>
            <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1, opacity: 0.92 }}>
              {[
                ['solar:card-bold-duotone', `Adm ${student.admnNo}`],
                ['solar:square-academic-cap-bold-duotone', student.className],
                ['solar:users-group-rounded-bold-duotone', batch.batchName],
                ['solar:book-bookmark-bold-duotone', batch.courseName],
                ['solar:calendar-bold-duotone', student.assignedOn ? `In batch since ${fmtDate(student.assignedOn)}` : null],
              ]
                .filter(([, text]) => text)
                .map(([icon, text]) => (
                  <Stack key={icon} direction="row" gap={0.6} alignItems="center">
                    <Icon icon={icon} width={17} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {text}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </Box>
          <Stack direction="row" gap={2.5} alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,.95)', color: RT.ink, borderRadius: 4, px: 2.5, py: 1.75 }}>
            <AttendanceRing value={summary.attendance} size={92} thickness={5.5} label="student" />
            <Stack gap={0.75}>
              <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 600 }}>
                Batch average
              </Typography>
              <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 24, fontWeight: 700, lineHeight: 1, ...numberSx }}>{fmtPct(summary.batchAttendance)}</Typography>
              <DeltaChip mine={summary.attendance} batch={summary.batchAttendance} />
            </Stack>
          </Stack>
        </Stack>
      </Card>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
        {cards.map((card) => (
          <GradientStatCard key={card.label} {...card} />
        ))}
      </Box>

      <Panel icon="solar:book-2-bold-duotone" color="#0EA5E9" title="Subject wise" subtitle="This student's attendance per subject against the batch average">
        {subjects.length ? (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' } }}>
            {subjects.map((subject) => (
              <SubjectCompareCard key={subject.subjectEnrollmentId} subject={subject} />
            ))}
          </Box>
        ) : (
          <EmptyBlock title="No subjects assigned" />
        )}
      </Panel>

      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        <Panel icon="solar:graph-new-up-bold-duotone" title="Monthly trend" subtitle="Sessions and attendance % per month">
          {trend.length ? <TrendChart points={trend} sessionsLabel="Sessions" /> : <EmptyBlock title="No sessions in this range" />}
        </Panel>
        <Panel icon="solar:calendar-date-bold-duotone" color={RT.info} title="By weekday">
          <WeekdayChart weekday={weekday} />
        </Panel>
      </Box>

      <Panel icon="solar:history-bold-duotone" color={RT.success} title="Session history" subtitle="Every session of this student's subjects since joining, latest first">
        <SessionTimeline sessions={sessions} subjects={subjects} />
      </Panel>
      <DataCheckNote check={data.check} />
    </Stack>
  );
}
